import { isZymedApp } from "./appConfig";
import { sendMessageToParent } from "./helper";

declare global {
  interface Window {
    NATIVE_APP_VERSION?: string;
    NATIVE_APP_BUILD?: string;
    NATIVE_APP_PLATFORM?: string;
  }
}

/**
 * "Update the app" banner for ZyMedElite users on outdated native builds.
 *
 * Native builds from 28e6913 onward inject window.NATIVE_APP_VERSION before the PWA
 * loads; older builds don't, so a missing value means "outdated native app".
 *
 * Keep `enabled: false` until a native build that injects NATIVE_APP_VERSION is live
 * on BOTH stores — otherwise every current user sees a banner with nothing to update to.
 * Then set `enabled: true` and `latestVersion` to the live store version.
 */
export const NATIVE_UPDATE_BANNER = {
  enabled: false,
  latestVersion: "1.4.2",
};

const STORE_URLS = {
  ios: "https://apps.apple.com/in/app/id6472478226",
  android: "https://play.google.com/store/apps/details?id=com.dchpl.zymedelite",
};

const DISMISS_KEY = "native_update_banner_dismissed_at";
const SNOOZE_MS = 24 * 60 * 60 * 1000;

const isOlderThan = (installed: string, target: string) => {
  const a = installed.split(".").map((part) => Number(part) || 0);
  const b = target.split(".").map((part) => Number(part) || 0);
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    const diff = (a[i] || 0) - (b[i] || 0);
    if (diff !== 0) return diff < 0;
  }
  return false;
};

const isSnoozed = () => {
  try {
    const dismissedAt = Number(localStorage.getItem(DISMISS_KEY));
    return !!dismissedAt && Date.now() - dismissedAt < SNOOZE_MS;
  } catch {
    return false;
  }
};

export const shouldShowNativeUpdateBanner = (): boolean => {
  if (!NATIVE_UPDATE_BANNER.enabled || !window.ReactNativeWebView || !isZymedApp()) {
    return false;
  }
  const installed = window.NATIVE_APP_VERSION;
  const outdated = !installed || isOlderThan(installed, NATIVE_UPDATE_BANNER.latestVersion);
  return outdated && !isSnoozed();
};

export const snoozeNativeUpdateBanner = () => {
  try {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  } catch {
    // Non-critical: banner shows again on next load.
  }
};

const detectPlatform = (): "ios" | "android" => {
  if (window.NATIVE_APP_PLATFORM === "ios" || window.NATIVE_APP_PLATFORM === "android") {
    return window.NATIVE_APP_PLATFORM;
  }
  const ua = navigator.userAgent;
  // iPadOS reports a Mac UA; touch points tell it apart from a desktop Mac.
  const isIOS = /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
  return isIOS ? "ios" : "android";
};

/** Old native builds handle openExternalLink with Linking.openURL → opens the store app. */
export const openNativeAppStore = () => {
  sendMessageToParent("openExternalLink", { url: STORE_URLS[detectPlatform()] });
};

/** "v1.4.2 (10258)" when running inside a native build that reports its version, else null. */
export const getNativeAppVersionLabel = (): string | null => {
  const version = window.NATIVE_APP_VERSION;
  if (!window.ReactNativeWebView || !version) return null;
  const build = window.NATIVE_APP_BUILD;
  return build ? `v${version} (${build})` : `v${version}`;
};
